import React, { useCallback, useMemo, useState } from 'react';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import {
  DarwinListView,
  StatusChip,
  StatusChipColors,
} from 'components/darwin-ui';
import { getUomsColumns } from './columns';
import useTableControls from 'hooks/useTableControls';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import {
  useGetUnitsOfMeasure,
  useEditUoms,
} from 'api/services/unitsOfMeasureService';
import { EditedUomItem, UnitOfMeasure } from 'types/unitOfMeasureTypes';

type UomDraft = {
  aliases?: string[];
  active?: boolean;
};

const fieldMapping = {
  symbol: 'abbreviation',
  active: 'status',
};

export const UnitsOfMeasure = () => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const editUomsMutation = useEditUoms();

  const { searchQuery, sortOrder, onTableChange } = useTableControls({
    defaultSortBy: 'name',
    fieldMapping,
  });

  const {
    data: uomsData,
    isLoading: isLoadingReports,
    isFetching: isFetchingReports,
    isPending: isPendingReports,
    error: uomsError,
  } = useGetUnitsOfMeasure({
    sortOrder,
    limit: 100,
  });

  // Unified state for all edits
  const [editedItems, setEditedItems] = useState<Record<string, UomDraft>>({});

  // Show error notification
  if (uomsError?.message) {
    openNotification('Error', t('uoms.loading-error'), 'error');
  }

  const tableData = useMemo(() => uomsData?.data ?? [], [uomsData]);
  const isLoading = isLoadingReports || isFetchingReports || isPendingReports;

  // Clear edits when fresh data matches our edited values
  React.useEffect(() => {
    if (tableData.length > 0 && Object.keys(editedItems).length > 0) {
      const shouldClearEdits = Object.keys(editedItems).every(id => {
        const original = tableData.find(item => String(item.id) === id);
        const edited = editedItems[id];
        if (!original || !edited) return false;

        const aliasesMatch =
          edited.aliases === undefined ||
          JSON.stringify(original.aliases?.sort()) ===
            JSON.stringify(edited.aliases.sort());
        const activeMatch =
          edited.active === undefined || original.active === edited.active;

        return aliasesMatch && activeMatch;
      });

      if (shouldClearEdits) {
        setEditedItems({});
      }
    }
  }, [tableData, editedItems]);

  const handleAliasesChange = useCallback(
    (id: string, newAliases: string[]) => {
      setEditedItems(prev => {
        const original = tableData.find(item => String(item.id) === id);
        if (!original) return prev;

        const originalAliases = original.aliases ?? [];
        const hasChanged =
          JSON.stringify(originalAliases.sort()) !==
          JSON.stringify(newAliases.sort());

        if (!hasChanged) {
          // Remove aliases edit if reverted to original
          const { aliases, ...rest } = prev[id] || {};
          if (Object.keys(rest).length === 0) {
            const { [id]: _removed, ...remaining } = prev;
            return remaining;
          }
          return { ...prev, [id]: rest };
        }

        return {
          ...prev,
          [id]: {
            ...prev[id],
            aliases: newAliases,
          },
        };
      });
    },
    [tableData]
  );

  const handleStatusToggle = useCallback(
    (id: string) => {
      setEditedItems(prev => {
        const original = tableData.find(item => String(item.id) === id);
        if (!original) return prev;

        const currentActive = prev[id]?.active ?? original.active;
        const newActive = !currentActive;

        if (newActive === original.active) {
          // Remove status edit if reverted to original
          const { active, ...rest } = prev[id] || {};
          if (Object.keys(rest).length === 0) {
            const { [id]: _removed, ...remaining } = prev;
            return remaining;
          }
          return { ...prev, [id]: rest };
        }

        return {
          ...prev,
          [id]: {
            ...prev[id],
            active: newActive,
          },
        };
      });
    },
    [tableData]
  );

  // Memoized payload - only edited items
  const savePayload = useMemo(() => {
    const units: EditedUomItem[] = Object.entries(editedItems)
      .map(([unitId, draft]) => {
        const original = tableData.find(item => String(item.id) === unitId);
        if (!original) return null;

        return {
          unitId,
          aliases:
            draft.aliases !== undefined ? draft.aliases : original.aliases,
          active: draft.active !== undefined ? draft.active : original.active,
        };
      })
      .filter(Boolean) as EditedUomItem[];

    return { units };
  }, [editedItems, tableData]);

  const handleSave = useCallback(async () => {
    if (savePayload.units.length === 0) return;

    try {
      await editUomsMutation.mutateAsync({ uomsData: savePayload });
      openNotification(t('success'), t('uoms.saved-success'), 'success');
    } catch (error) {
      openNotification(t('error'), t('uoms.save-error'), 'error');
    }
  }, [savePayload, editUomsMutation, openNotification, t]);

  const rowActions = useCallback(
    (item: UnitOfMeasure) => {
      const key = String(item.id);
      const isCurrentlyActive = editedItems[key]?.active ?? item.active;

      return [
        {
          key: 'toggleActive',
          label: (
            <StatusChip
              label={
                isCurrentlyActive
                  ? t('uoms.actions.deactivate')
                  : t('uoms.actions.activate')
              }
              color={
                isCurrentlyActive
                  ? StatusChipColors.red
                  : StatusChipColors.green
              }
            />
          ),
          onClick: () => handleStatusToggle(key),
        },
      ];
    },
    [editedItems, handleStatusToggle, t]
  );

  const columns = useMemo(
    () =>
      getUomsColumns(
        t,
        rowActions,
        searchQuery,
        editedItems as unknown as Record<string, EditedUomItem>, // only aliases/active used
        handleAliasesChange
      ),
    [t, rowActions, searchQuery, editedItems, handleAliasesChange]
  );

  const getInactiveRow = useCallback(
    (uom: UnitOfMeasure) => {
      return !(editedItems[String(uom.id)]?.active ?? uom.active);
    },
    [editedItems]
  );

  const hasChanges = Object.keys(editedItems).length > 0;
  const isSaving = editUomsMutation.isPending;

  return (
    <>
      <ContentHeader
        title={t('uoms.title')}
        onSave={handleSave}
        saveDisabled={!hasChanges || isSaving}
      />

      <DarwinListView<UnitOfMeasure>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        table={{
          onChange: onTableChange,
          scrollable: false,
        }}
        behavior={{ isRowInactive: getInactiveRow }}
      />
    </>
  );
};
